import { useMemo, useState } from "react";
import {
  rangeFromPreset,
  todayRange,
  type DateRangePreset,
  type PartnerDashboardDateRange,
} from "../lib/partnerDashboardDate";

/**
 * Shared date-range state for Overview (live, defaults to Today) and report
 * pages (default to a wider window so periods with no activity are not empty).
 */
export function usePartnerDashboardDateRange(
  initialPreset: DateRangePreset = "today"
) {
  const [range, setRange] = useState<PartnerDashboardDateRange>(() =>
    initialPreset === "today" ? todayRange() : rangeFromPreset(initialPreset)
  );

  const setToday = () => {
    // Always rebuild bounds so "Today" stays correct across midnight / re-click.
    setRange(todayRange());
  };

  const setCustom = (start: Date, end: Date) => {
    const s = new Date(start);
    s.setHours(0, 0, 0, 0);
    const e = new Date(end);
    e.setHours(23, 59, 59, 999);
    setRange({
      mode: "custom",
      start: s,
      end: e < s ? new Date(s.getTime() + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59999) : e,
    });
  };

  return useMemo(
    () => ({
      range,
      setToday,
      setCustom,
      isToday: range.mode === "today",
    }),
    [range]
  );
}
